import { environment } from '../config/environment';
import { useApiClient } from './api';
import { TaxReport, TaxReportDetailed, TaxReportSummary } from './tax-reports.types';
import { sortTaxReportsByYear, transformTaxReport } from './transformers';

export const useTaxReportService = () => {
    const apiClient = useApiClient();

    const getAll = async () => {
        try {
            const response = await apiClient.get<TaxReport[]>(`${environment.ctLiteApiUrl}/tax-reports/list`);
            return sortTaxReportsByYear(transformTaxReport(response.data));
        } catch (error) {
            console.error('Error getting all tax reports:', error);
            throw error;
        }
    };

    const getTaxReportDetailed = async (taxReportId: number) => {
        try {
            const response = await apiClient.get<TaxReportDetailed>(
                `${environment.ctLiteApiUrl}/tax-reports/detailed?tax_report_id=${taxReportId}`
            );
            return response.data;
        } catch (error) {
            console.error('Error getting detailed tax report:', error);
            throw error;
        }
    };

    const reportsFullExportSummary = async (taxReportId: number) => {
        try {
            const response = await apiClient.get<TaxReportSummary[]>(
                `${environment.ctLiteApiUrl}/tax-reports/export/summary?tax_report_id=${taxReportId}`
            );
            return response.data;
        } catch (error) {
            console.error('Error getting full export summary:', error);
            throw error;
        }
    };

    const downloadPdf = async (parameters: any) => {
        download(parameters, 'application/pdf');
    };

    const downloadExcel = async (parameters: any) => {
        download(parameters, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    };

    const download = async (
        parameters: any,
        format: 'application/pdf' | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ) => {
        try {
            const response = await apiClient.post(
                `${environment.ctLiteApiUrl}/tax-reports/download?tax_report_id=${parameters.id}`,
                parameters,
                {
                    responseType: 'blob',
                    headers: { Accept: format },
                }
            );

            const blob = new Blob([response.data], { type: format });
            const downloadURL = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadURL;
            link.download = parameters.filename;
            link.click();
        } catch (error) {
            console.error('Error downloading tax report:', error);
            throw error;
        }
    };

    const deleteTaxReport = async (taxReportId: number) => {
        try {
            await apiClient.delete<void>(`${environment.ctLiteApiUrl}/tax-reports/${taxReportId}`);
        } catch (error) {
            console.error('Error deleting tax report:', error);
            throw error;
        }
    };

    return {
        getAll,
        getTaxReportDetailed,
        reportsFullExportSummary,
        downloadPdf,
        downloadExcel,
        delete: deleteTaxReport,
    };
};
